import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useGetGymAiAssistantPublic, useCompleteStudentOnboarding, useGetStudentGyms, useListPublicGyms, useAddStudentGym } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, Bot, Calendar, Clock, ArrowRight, Loader2, MapPin, CheckCircle, Search, Building2, Check } from "lucide-react";
import { Link } from "wouter";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const AVATAR_PRESETS: Record<string, string> = {
  "preset:coach-male": "\u{1F3CB}\uFE0F",
  "preset:coach-female": "\u{1F938}",
  "preset:sensei": "\u{1F94B}",
  "preset:bot": "\u{1F916}",
  "preset:zen": "\u{1F9D8}",
};

type ChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
  trainerResults?: TrainerSearchResult | null;
};

type TrainerSlot = {
  start: string;
  end: string;
  trainerId: string;
};

type TrainerMatch = {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  specialties: string[];
  hourlyRate?: number | null;
  gender?: string | null;
  availableSlots: TrainerSlot[];
  totalAvailableSlots: number;
};

type TrainerSearchResult = {
  availableSpecialties: string[];
  matches: TrainerMatch[];
  otherTrainers: TrainerMatch[];
  totalTrainersAtGym: number;
};

function AssistantAvatar({ avatarUrl, name, size = "sm" }: { avatarUrl: string | null; name: string; size?: "sm" | "lg" }) {
  const sizeClass = size === "lg" ? "w-12 h-12" : "w-8 h-8";
  const textSize = size === "lg" ? "text-xl" : "text-sm";
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  if (avatarUrl && !avatarUrl.startsWith("preset:") && !imgError) {
    return (
      <div className={`${sizeClass} rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden ring-2 ring-primary/20`}>
        <img src={avatarUrl} alt={name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
      </div>
    );
  }

  const emoji = avatarUrl && !imgError ? AVATAR_PRESETS[avatarUrl] : null;
  return (
    <div className={`${sizeClass} rounded-full bg-secondary flex items-center justify-center ${textSize} shrink-0 ring-2 ring-primary/20`}>
      {emoji || name.charAt(0).toUpperCase()}
    </div>
  );
}

function TrainerCard({
  trainer,
  onSelectSlot,
  bookingInProgress,
}: {
  trainer: TrainerMatch;
  onSelectSlot: (trainerId: string, slot: TrainerSlot) => void;
  bookingInProgress: boolean;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-base font-bold text-white shrink-0 overflow-hidden">
          {trainer.avatarUrl ? (
            <img src={trainer.avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            trainer.firstName[0]
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-white text-sm">
            {trainer.firstName} {trainer.lastName}
          </h4>
          <div className="flex flex-wrap gap-1 mt-1">
            {trainer.specialties.map((s) => (
              <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                {s}
              </span>
            ))}
          </div>
          {trainer.bio && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{trainer.bio}</p>
          )}
          {trainer.hourlyRate && (
            <p className="text-xs text-muted-foreground mt-1">${trainer.hourlyRate}/hr</p>
          )}
        </div>
      </div>
      {trainer.availableSlots.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {trainer.totalAvailableSlots} slot{trainer.totalAvailableSlots !== 1 ? "s" : ""} available
          </p>
          <div className="flex flex-wrap gap-1.5">
            {trainer.availableSlots.slice(0, 6).map((slot, i) => {
              const startDate = new Date(slot.start);
              return (
                <button
                  key={i}
                  onClick={() => onSelectSlot(trainer.id, slot)}
                  disabled={bookingInProgress}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 bg-white/5 hover:bg-primary/20 hover:border-primary/30 hover:text-primary transition-all disabled:opacity-50"
                >
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(startDate, "EEE, MMM d")}
                    <Clock className="w-3 h-3 ml-1" />
                    {format(startDate, "h:mm a")}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function OnboardingAgent({ onComplete }: { onComplete: () => void }) {
  const { user, refreshUser } = useAuth();
  const gymId = user?.gymId || "";

  const { data: assistantConfig } = useGetGymAiAssistantPublic(gymId, {
    query: { enabled: !!gymId },
  });

  const completeMutation = useCompleteStudentOnboarding();
  const { toast } = useToast();

  const [phase, setPhase] = useState<"gym-confirm" | "chat">("gym-confirm");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [gymSearch, setGymSearch] = useState("");
  const [selectedGymId, setSelectedGymId] = useState<string | null>(null);
  const [isAssociatingGym, setIsAssociatingGym] = useState(false);
  const hasGym = !!user?.gymId;
  const { data: publicGyms, isLoading: gymsLoading } = useListPublicGyms({ query: { enabled: !hasGym } });
  const addGymMutation = useAddStudentGym();

  const filteredGyms = useMemo(() => {
    if (!publicGyms) return [];
    if (!gymSearch.trim()) return publicGyms;
    const q = gymSearch.toLowerCase();
    return publicGyms.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.address && g.address.toLowerCase().includes(q))
    );
  }, [publicGyms, gymSearch]);

  const handleSelectGymAndAssociate = async (gymId: string) => {
    if (selectedGymId === gymId) {
      setSelectedGymId(null);
      return;
    }
    setSelectedGymId(gymId);
    setIsAssociatingGym(true);
    try {
      await addGymMutation.mutateAsync({ data: { gymId } });
      await refreshUser();
    } catch {
      setSelectedGymId(null);
      toast({
        title: "Failed to select gym",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAssociatingGym(false);
    }
  };

  const assistantName = assistantConfig?.name || "Assistant";
  const assistantAvatar = assistantConfig?.avatarUrl || null;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleDismiss = async () => {
    try {
      await completeMutation.mutateAsync();
      await refreshUser();
      onComplete();
    } catch {
      onComplete();
    }
  };

  const handleConfirmGym = () => {
    setPhase("chat");
    sendMessage("Hi! I just joined and I'd like to learn about booking a private lesson.", true);
  };

  const sendMessage = async (content: string, isInitial = false) => {
    if (!content.trim() || isStreaming) return;

    const userMessage: ChatMessage = { role: "user", content };
    const newMessages = isInitial ? [userMessage] : [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsStreaming(true);

    try {
      const token = localStorage.getItem("token");
      const apiBase = import.meta.env.VITE_API_URL || "";

      const response = await fetch(`${apiBase}/api/onboarding/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
          gymId,
        }),
      });

      if (!response.ok) {
        throw new Error("Chat request failed");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let assistantContent = "";
      let latestTrainerResults: TrainerSearchResult | null = null;

      const assistantMessage: ChatMessage = { role: "assistant", content: "" };
      setMessages((prev) => [...prev, assistantMessage]);

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.content) {
              assistantContent += data.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: assistantContent,
                  trainerResults: latestTrainerResults,
                };
                return updated;
              });
            }

            if (data.toolResult?.type === "search_trainers") {
              latestTrainerResults = data.toolResult.data;
            }

            if (data.done) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: assistantContent,
                  trainerResults: latestTrainerResults,
                };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I ran into an issue. Please try again or skip the onboarding.",
        },
      ]);
    } finally {
      setIsStreaming(false);
      inputRef.current?.focus();
    }
  };

  const handleSelectSlot = async (trainerId: string, slot: TrainerSlot) => {
    setBookingInProgress(true);
    try {
      const token = localStorage.getItem("token");
      const apiBase = import.meta.env.VITE_API_URL || "";
      const startDate = new Date(slot.start);
      const res = await fetch(`${apiBase}/api/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          trainerId,
          gymId,
          proposedStart: slot.start,
          durationMinutes: 60,
          mode: "IN_PERSON",
          paymentType: "SINGLE",
        }),
      });

      const data = await res.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      if (res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Your session has been booked for ${format(startDate, "EEEE, MMMM d 'at' h:mm a")}! You can view your upcoming sessions on your dashboard. Enjoy your training!`,
          },
        ]);
        setTimeout(() => handleDismiss(), 3000);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `I wasn't able to book that slot: ${data.error || "Unknown error"}. You can try another slot or check the full schedule.`,
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "There was an issue creating the booking. You can try again or visit the booking page directly.",
        },
      ]);
    } finally {
      setBookingInProgress(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      sendMessage(input);
    }
  };

  if (phase === "gym-confirm") {
    return (
      <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-6">
              <Bot className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-display font-bold text-white mb-2">
              Welcome to OPEN MAT
            </h1>
            <p className="text-muted-foreground text-lg">
              Let's get you set up with your first private lesson
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-4">
            <p className="text-white/80 text-sm">
              {hasGym
                ? "We'll connect you with your gym's AI scheduling assistant to help you find and book your first private lesson. First, let's confirm your home gym:"
                : "We'll connect you with your gym's AI scheduling assistant to help you find and book your first private lesson. First, select your home gym:"}
            </p>

            {hasGym ? (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                <MapPin className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Home Gym</p>
                  <GymNameDisplay gymId={user!.gymId!} />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search gyms by name or location..."
                    value={gymSearch}
                    onChange={(e) => setGymSearch(e.target.value)}
                    className="pl-11 bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2 scrollbar-thin">
                  {gymsLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />
                      ))}
                    </div>
                  ) : filteredGyms.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      {publicGyms && publicGyms.length === 0 ? (
                        <>
                          <p className="text-sm font-medium text-white mb-1">No gyms available yet</p>
                          <p className="text-xs text-muted-foreground">Check back soon — new gyms are being added regularly.</p>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">No gyms match your search</p>
                      )}
                    </div>
                  ) : (
                    filteredGyms.map((gym) => (
                      <button
                        key={gym.id}
                        type="button"
                        disabled={isAssociatingGym}
                        onClick={() => handleSelectGymAndAssociate(gym.id)}
                        className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                          selectedGymId === gym.id
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                        }`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                          {gym.logoUrl ? (
                            <img src={gym.logoUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Building2 className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{gym.name}</p>
                          {gym.address && (
                            <p className="text-xs text-muted-foreground truncate">{gym.address}</p>
                          )}
                        </div>
                        {selectedGymId === gym.id && (
                          isAssociatingGym ? (
                            <Loader2 className="w-5 h-5 text-primary shrink-0 animate-spin" />
                          ) : (
                            <Check className="w-5 h-5 text-primary shrink-0" />
                          )
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="flex-1 h-14 text-lg border-white/10 hover:bg-white/5 rounded-xl"
            >
              Skip for now
            </Button>
            <Button
              onClick={handleConfirmGym}
              disabled={!user?.gymId || isAssociatingGym}
              className="flex-1 h-14 text-lg font-bold bg-primary text-black rounded-xl"
            >
              That's my gym <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-card">
        <div className="flex items-center gap-3">
          <AssistantAvatar avatarUrl={assistantAvatar} name={assistantName} size="sm" />
          <div>
            <h3 className="font-bold text-white text-sm">{assistantName}</h3>
            <p className="text-xs text-muted-foreground">AI Scheduling Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {messages.length >= 2 && !isStreaming && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDismiss}
              className="border-primary/30 text-primary hover:bg-primary/10 text-xs gap-1"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Done
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.map((msg, i) => (
          <div key={i}>
            {msg.role === "user" ? (
              <div className="flex justify-end">
                <div className="bg-primary text-black rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[80%] font-medium">
                  {msg.content}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <AssistantAvatar avatarUrl={assistantAvatar} name={assistantName} />
                  <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-white/90 max-w-[80%] border border-white/5 whitespace-pre-wrap">
                    {msg.content}
                    {isStreaming && i === messages.length - 1 && !msg.content && (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" /> Thinking...
                      </span>
                    )}
                  </div>
                </div>
                {msg.trainerResults && msg.trainerResults.matches.length > 0 && (
                  <div className="ml-11 space-y-2">
                    {msg.trainerResults.matches.map((trainer) => (
                      <TrainerCard
                        key={trainer.id}
                        trainer={trainer}
                        onSelectSlot={handleSelectSlot}
                        bookingInProgress={bookingInProgress}
                      />
                    ))}
                    <div className="pt-2">
                      <Link href="/find-trainer">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-white/10 text-muted-foreground hover:text-white text-xs"
                          onClick={handleDismiss}
                        >
                          View Full Schedule <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
                {msg.trainerResults && msg.trainerResults.matches.length === 0 && (
                  <div className="ml-11">
                    <Link href="/find-trainer">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-primary/30 text-primary hover:bg-primary/10 text-xs"
                        onClick={handleDismiss}
                      >
                        Browse All Trainers <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="px-4 py-3 border-t border-white/10 bg-card flex gap-2"
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          disabled={isStreaming}
          className="flex-1 h-12 px-4 rounded-xl bg-background border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
        <Button
          type="submit"
          disabled={isStreaming || !input.trim()}
          className="h-12 w-12 rounded-xl bg-primary text-black p-0"
        >
          {isStreaming ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </form>
    </div>
  );
}

function GymNameDisplay({ gymId }: { gymId: string }) {
  const { data: gyms } = useGetStudentGyms({
    query: { enabled: !!gymId },
  });

  const gymName = gyms?.find((g) => g.gymId === gymId)?.gym?.name;

  return (
    <p className="text-lg font-bold text-white">
      {gymName || "Loading..."}
    </p>
  );
}
